import { Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CertificationPillar } from '@/features/marketing/constants/certificationsContent'

export default function CertCard({
  pillar,
  className = '',
  tall = false,
}: {
  pillar: CertificationPillar;
  className?: string;
  tall?: boolean;
}) {
  const { t } = useTranslation()
  return (
    <div
      className={`group relative rounded-[24px] p-[1px] overflow-hidden transition-all duration-500 ${className}`}
      style={{
        background:
          'linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.04) 35%, rgba(124,58,237,0.3) 100%)',
      }}
    >
      <div className="relative rounded-[23px] bg-gradient-to-br from-[#0a0a12] via-[#0d0d18] to-[#0a0a12] p-8 flex flex-col h-full overflow-hidden">
        {/* Aurora glow at the bottom */}
        <div
          className="pointer-events-none absolute -bottom-24 left-1/2 -translate-x-1/2 w-[130%] h-[240px] opacity-85 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background:
              'radial-gradient(ellipse 55% 100% at 50% 100%, rgba(76,130,246,0.6) 0%, rgba(124,58,237,0.45) 35%, rgba(167,85,247,0.25) 55%, transparent 75%)',
            filter: 'blur(35px)',
          }}
          aria-hidden
        />

        {/* Subtle noise overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")",
          }}
          aria-hidden
        />

        {/* icon — same style as CardIcon used across the page */}
        <div className="relative z-10 w-10 h-10 rounded-lg bg-[#7c3aed]/15 border border-[#7c3aed]/25 flex items-center justify-center text-[#d2bbff] shrink-0 mb-6">
          <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{pillar.icon}</span>
        </div>

        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col">
          <h3
            className={`font-headline font-bold text-white leading-tight mb-3 ${
              tall ? 'text-[26px]' : 'text-[22px]'
            }`}
          >
            {pillar.title}
          </h3>

          {tall && (
            <p className="text-white/55 text-[14px] leading-relaxed mb-5 line-clamp-4">
              {pillar.body}
            </p>
          )}
          {!tall && (
            <p className="text-white/55 text-[13px] leading-relaxed mb-4 line-clamp-2">
              {pillar.body}
            </p>
          )}

          <Link
            to="/certifications"
            className="mt-auto inline-flex items-center gap-1.5 text-white text-sm font-medium hover:text-[#a78bfa] transition-colors group/link w-fit"
          >
            {t('home.certCard.learnMore')}
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
